import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Dropdown, message, Modal, Space } from 'antd';
import { LimsNavIcon } from './LimsNavIcon';
import {
  getNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  deleteAllNotifications,
  Notification,
} from '../services/api';
import { literatureNotificationService } from '../services/literature/notificationService';
import dayjs from 'dayjs';
import '../styles/resourceManagement.css';

const SHARE_INVITE_TYPES = new Set<string>(['shared_folder_invite', 'shared_experiment_notebook_invite']);

function isPendingShareInvite(n: Notification): boolean {
  return n.status === 'pending' && SHARE_INVITE_TYPES.has(String(n.type));
}

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 检测屏幕尺寸变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 获取未读数量
  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadCount();
      if (response.success && response.data) {
        setUnreadCount(response.data.count);
      }
    } catch (error) {
      console.error('获取未读数量失败:', error);
    }
  };

  // 获取通知列表
  const fetchNotifications = async () => {
    try {
      const response = await getNotifications({ limit: 10 });
      if (response.success && response.data) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('获取通知失败:', error);
    }
  };

  // 标记为已读
  const handleMarkAsRead = async (notification: Notification) => {
    if (notification.isRead) return;
    
    try {
      await markNotificationAsRead(notification._id);
      setNotifications(prev => 
        prev.map(n => 
          n._id === notification._id 
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      message.error('标记已读失败');
    }
  };

  // 标记所有为已读
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => 
        prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
      message.success('所有通知已标记为已读');
    } catch (error) {
      message.error('标记所有已读失败');
    }
  };

  // 删除通知
  const handleDelete = async (notificationId: string, wasUnread: boolean) => {
    try {
      const res = await deleteNotification(notificationId);
      if (!res.success) {
        message.error(typeof res.error === 'string' ? res.error : '删除通知失败');
        return;
      }
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
      void fetchUnreadCount();
      message.success('通知已删除');
    } catch (error) {
      message.error('删除通知失败');
    }
  };

  const handleDeleteAll = () => {
    Modal.confirm({
      title: '删除全部通知',
      content: '将删除您账户下的全部通知（含未在列表中显示的历史通知），确定继续吗？',
      okText: '全部删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await deleteAllNotifications();
          if (!res.success) {
            message.error(typeof res.error === 'string' ? res.error : '删除全部通知失败');
            return;
          }
          setNotifications([]);
          setUnreadCount(0);
          void fetchUnreadCount();
          message.success('全部通知已删除');
        } catch {
          message.error('删除全部通知失败');
        }
      },
    });
  };

  const getTypePillClass = (type: string) => {
    const map: Record<string, string> = {
      system: 'system',
      reservation: 'reservation',
      approval: 'approval',
      maintenance: 'maintenance',
      training: 'training',
      equipment: 'equipment',
      announcement: 'announcement',
      lab_activity: 'lab_activity',
      shared_folder_invite: 'approval',
      shared_folder_accepted: 'approval',
      shared_folder_rejected: 'approval',
      shared_experiment_notebook_invite: 'approval',
      shared_experiment_notebook_accepted: 'approval',
      shared_experiment_notebook_rejected: 'approval'
    };
    return map[type] || 'default';
  };

  const getPriorityPillClass = (priority: string) => {
    const map: Record<string, string> = {
      low: 'low',
      medium: 'medium',
      high: 'high',
      urgent: 'urgent'
    };
    return map[priority] || 'medium';
  };

  // 获取类型中文名称
  const getTypeName = (type: string) => {
    const names: { [key: string]: string } = {
      system: '系统通知',
      reservation: '预约通知',
      approval: '审批通知',
      maintenance: '维护通知',
      training: '培训通知',
      equipment: '设备通知',
      announcement: '公告通知',
      lab_activity: '实验室活动',
      shared_folder_invite: '文献共享邀请',
      shared_folder_accepted: '文献共享',
      shared_folder_rejected: '文献共享',
      shared_experiment_notebook_invite: '实验共享邀请',
      shared_experiment_notebook_accepted: '实验共享',
      shared_experiment_notebook_rejected: '实验共享'
    };
    return names[type] || type;
  };

  // 获取优先级中文名称
  const getPriorityName = (priority: string) => {
    const names: { [key: string]: string } = {
      low: '低',
      medium: '中',
      high: '高',
      urgent: '紧急'
    };
    return names[priority] || priority;
  };

  useEffect(() => {
    fetchUnreadCount();
    fetchNotifications();

    // 设置定时刷新未读数量
    intervalRef.current = setInterval(fetchUnreadCount, 30000); // 30秒刷新一次

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleOpenChange = (open: boolean) => {
    setVisible(open);
    if (open) {
      fetchNotifications();
      fetchUnreadCount();
    }
  };

  const handleAcceptShareInvite = async (id: string) => {
    try {
      await literatureNotificationService.acceptInvite(id);
      message.success('已接受共享邀请');
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (e: unknown) {
      message.error((e as Error).message || '接受失败');
    }
  };

  const handleRejectShareInvite = async (id: string) => {
    try {
      await literatureNotificationService.rejectInvite(id);
      message.success('已拒绝');
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (e: unknown) {
      message.error((e as Error).message || '操作失败');
    }
  };

  const renderNotificationPanel = () => (
    <div className="lims-notification-dropdown">
      <div className="lims-notification-dropdown__header">
        <div className="lims-notification-dropdown__header-top">
          <h2 className="lims-notification-dropdown__title">通知中心</h2>
          <div className="lims-notification-dropdown__meta">
            {unreadCount > 0 && (
              <span className="lims-notification-dropdown__unread-badge">{unreadCount} 未读</span>
            )}
            {unreadCount > 0 && (
              <button
                type="button"
                className="lims-notification-dropdown__mark-all"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleMarkAllAsRead();
                }}
              >
                <LimsNavIcon name="check" sizePx={16} aria-hidden />
                全部已读
              </button>
            )}
            {notifications.length > 0 && (
              <button
                type="button"
                className="lims-notification-dropdown__delete-all"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteAll();
                }}
              >
                <LimsNavIcon name="delete" sizePx={16} aria-hidden />
                全部删除
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="lims-notification-dropdown__scroll">
        {notifications.length === 0 ? (
          <div className="lims-notification-dropdown__empty">暂无通知</div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification._id}
              role="button"
              tabIndex={0}
              className={`lims-notification-dropdown__item${notification.isRead ? '' : ' is-unread'}`}
              onClick={() => {
                if (isPendingShareInvite(notification)) return;
                void handleMarkAsRead(notification);
                if (notification.actionUrl) {
                  setVisible(false);
                  try {
                    const url = new URL(notification.actionUrl, window.location.origin);
                    if (url.origin === window.location.origin) {
                      navigate(url.pathname + url.search);
                    } else {
                      window.location.href = notification.actionUrl!;
                    }
                  } catch {
                    if (notification.actionUrl!.startsWith('/')) {
                      navigate(notification.actionUrl!);
                    }
                  }
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  (e.currentTarget as HTMLDivElement).click();
                }
              }}
            >
              <div className="lims-notification-dropdown__item-head">
                <p className="lims-notification-dropdown__item-title">{notification.title}</p>
                <div className="lims-notification-dropdown__pills">
                  <span className={`lims-notification-dropdown__pill lims-notification-dropdown__pill--${getTypePillClass(notification.type)}`}>
                    {getTypeName(notification.type)}
                  </span>
                  {notification.priority !== 'medium' && (
                    <span
                      className={`lims-notification-dropdown__pill lims-notification-dropdown__pill--priority-${getPriorityPillClass(notification.priority)}`}
                    >
                      {getPriorityName(notification.priority)}
                    </span>
                  )}
                </div>
              </div>
              <p className="lims-notification-dropdown__item-body">{notification.content}</p>
              {isPendingShareInvite(notification) ? (
                <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <Space size="small" wrap>
                    <Button type="primary" size="small" onClick={() => void handleAcceptShareInvite(notification._id)}>
                      接受
                    </Button>
                    <Button size="small" onClick={() => void handleRejectShareInvite(notification._id)}>
                      拒绝
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        setVisible(false);
                        navigate('/workbench/approval-management?tab=todo#am-share-invites');
                      }}
                    >
                      审批待办
                    </Button>
                  </Space>
                </div>
              ) : null}
              <div className="lims-notification-dropdown__item-foot">
                <span className="lims-notification-dropdown__item-time">
                  {dayjs(notification.createdAt).format('MM-DD HH:mm')}
                </span>
                <button
                  type="button"
                  className="lims-notification-dropdown__delete"
                  title="删除"
                  aria-label="删除通知"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDelete(notification._id, !notification.isRead);
                  }}
                >
                  <LimsNavIcon name="delete" sizePx={18} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      trigger={['click']}
      placement={isMobile ? 'bottom' : 'bottomRight'}
      open={visible}
      onOpenChange={handleOpenChange}
      overlayClassName="lims-notification-popup"
      overlayStyle={{
        margin: isMobile ? '0 16px' : undefined
      }}
      popupRender={() => renderNotificationPanel()}
    >
      <div
        style={{
          cursor: 'pointer',
          padding: '8px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Badge count={unreadCount} showZero={false} size="small" overflowCount={99}>
          <LimsNavIcon name="notifications" sizePx={22} style={{ color: 'var(--lims-secondary)' }} />
        </Badge>
      </div>
    </Dropdown>
  );
};

export default NotificationBell;
